import {
  Box,
  CircularProgress,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { makeStyles, styled } from "@mui/styles";
import { ReactSortable } from "react-sortablejs";
import { useDispatch, useSelector } from "react-redux";
import { DragIcon, InfoIcon, DragDisableIcon } from "../../shared/icon";
import VSNTypography from "../../shared/VSNTypography";
import { ApiContainer } from "../../utils/api";
import VSNToolTip from "../../shared/VSNToolTip";
import { getEndpoint } from "../../shared/CustomFunc";
import { SET_API_DATA } from "../../redux/constants";

const useStyles = makeStyles((theme) => ({
  assessmentTableCard: {
    width: "100%",
    flex: 1,
    backgroundColor: "var(--white)",
    alignItems: "center",
    cursor: "move",
    padding: 10,
    "& .MuiStack-root": {
      color: "var(--gray)",
      "&:first-child": {
        width: "64%",
      },
      "&:nth-child(2)": {
        width: "30%",
      },
    },
    "& svg": {
      flex: "none",
    },
    "&:hover": {
      backgroundColor: "#f1f0f0",
    },
  },
  assessmentTableCards: {
    paddingLeft: 5,
    minWidth: 1200,
  },
  assessmentTableCardsTitle: {
    alignItems: "center",
    padding: "8px 10px",
    "& p": {
      fontSize: 18,
      "& svg": {
        verticalAlign: "middle",
      },
      "&:first-child": {
        width: "80%",
      },
      "&:nth-child(2)": {
        width: "25%",
      },
    },
  },
}));

const AntSwitch = styled(Switch)(({ theme }) => ({
  "&.MuiSwitch-root": {
    width: 28,
    height: 16,
    padding: 0,
    display: "flex",
    "&:active": {
      "& .MuiSwitch-thumb": {
        width: 15,
      },
      "& .MuiSwitch-switchBase.Mui-checked": {
        transform: "translateX(9px)",
      },
    },
    "& .MuiSwitch-switchBase": {
      padding: 2,
      "&.Mui-checked": {
        transform: "translateX(12px)",
        color: "var(--white)",
        "& + .MuiSwitch-track": {
          opacity: 1,
          background: "var(--Tertiary)",
        },
        "&.Mui-disabled + .MuiSwitch-track": {
          opacity: 0.3,
          background: "var(--Tertiary)",
        },
      },
      "& + .MuiSwitch-track": {
        background: "#00000040",
      },
    },
    "& .MuiSwitch-thumb": {
      boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
      width: 12,
      height: 12,
      borderRadius: 6,
      transition: "all 0.4s ease-in-out",
    },
    "& .MuiSwitch-track": {
      borderRadius: 10,
      opacity: 1,
      boxSizing: "border-box",
    },
  },
}));

let count;
const Item = ({ item, draggable, classes, handleSwitchChange, mandatory }) => {
  count += 1;

  return (
    <Stack
      direction="row"
      sx={{ cursor: draggable ? "move !important" : "auto !important" }}
      className={classes.assessmentTableCard}
    >
      <Stack>
        <VSNTypography>{item?.waiver_name || ""}</VSNTypography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        flex="none"
        spacing={1}
        p="10px 20px 10px 10px"
        justifyContent="center"
      >
        <AntSwitch
          sx={item?.nonChangeable ? { pointerEvents: "none" } : {}}
          disabled={item?.nonChangeable || mandatory}
          checked={item?.is_required || false}
          onChange={(event) =>
            !item?.nonChangeable ? handleSwitchChange(event, item) : null
          }
          inputProps={{ "aria-label": "ant design" }}
        />
      </Stack>
    </Stack>
  );
};

export default function WaiversTab({ data, isLoading }) {
  count = 0;
  const classes = useStyles();
  const { api } = ApiContainer();
  const dispatch = useDispatch();
  const { organization_original_id, location_id, department_id, role } =
    useSelector((state) => state?.app?.userData?.user_profile || {});
  const frequencyOption = useSelector((state) => state?.api?.frequencyOption);

  const [waivers, setWaivers] = useState({});

  const payloadForEndPoint = {
    organization_original_id,
    location_id,
    department_id,
    role,
  };

  useEffect(() => {
    setWaivers(data || {});
  }, [data]);

  useEffect(() => {
    const optionData = async () => {
      const data = await api({
        method: "GET",
        endPoint: "assessment/frequency-dropdown",
        urlencoded: false,
      });
      dispatch({
        type: SET_API_DATA,
        payload: {
          frequencyOption: data?.data?.map((item) => ({
            value: item?.label,
            key: item?.label,
          })),
        },
      });
    };
    if (!frequencyOption) {
      optionData();
    }
  }, []);

  const onUpdateIndex = async ({ updatedItems }) => {
    // await api({
    //   method: "POST",
    //   endPoint: getEndpoint({
    //     ...payloadForEndPoint,
    //     endPoint: "waivers/change-order",
    //   }),
    //   data: { waiver: updatedItems },
    //   showToastMessage: true,
    //   urlencoded: false,
    // });
  };

  const handleSetData = (event) => {
    if (event?.oldIndex === event?.newIndex) return;
    const draggedItem = waivers?.waiversList?.[event.oldIndex];
    const newItems = [...(waivers?.waiversList || [])];
    newItems.splice(event.oldIndex, 1);
    newItems.splice(event.newIndex, 0, draggedItem);

    const updatedWaivers = { ...waivers, waiversList: newItems };
    setWaivers(updatedWaivers);

    const updatedItems = updatedWaivers.waiversList.map((item, index) => ({
      waiver_id: item.waiver_id,
      index,
    }));

    onUpdateIndex({ updatedItems });
  };

  const onSwitchChange = async (event, item) => {
    const updatedWaiver = {
      ...item,
      is_required: event.target.checked,
      enabled: event.target.checked,
    };
    setWaivers((prevState) => ({
      ...prevState,
      waiversList: prevState.waiversList.map((waiver) =>
        waiver.waiver_id === item.waiver_id ? updatedWaiver : waiver
      ),
    }));

    await api({
      method: "POST",
      endPoint: getEndpoint({
        ...payloadForEndPoint,
        endPoint: "waivers/is-required",
      }),
      data: {
        waiver_id: updatedWaiver.waiver_id,
        is_required: updatedWaiver.is_required,
      },
      showToastMessage: true,
      urlencoded: false,
    });
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          textAlign: "center",
          height: 45,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={24} color="inherit" />
      </Box>
    );
  }

  return (
    <>
      <Box className={classes.assessmentTableCards}>
        {waivers?.waiversList?.length > 0 ? (
          <>
            <Stack
              className={classes.assessmentTableCardsTitle}
              direction="row"
            >
              <Typography variant="body1">Waivers Name</Typography>

              <Typography variant="body1">
                Required
                <VSNToolTip
                  placement="top"
                  arrow
                  title="Mark waiver as required for all users. Drag the six-dot icon to reorder assessments."
                  slotProps={{
                    popper: {
                      sx: {
                        "& .MuiTooltip-tooltip": {
                          backgroundColor: "var(--white)",
                          boxShadow: "0px 6.447px 8px 0px rgba(0, 0, 0, 0.10)",
                          color: "var(--blackGray)",
                          py: 1,
                          px: 1.5,
                          fontSize: 14,
                          borderRadius: 3,
                          "& .MuiTooltip-arrow": {
                            color: "var(--white)",
                          },
                        },
                      },
                    },
                  }}
                >
                  <span>
                    {" "}
                    <InfoIcon />
                  </span>
                </VSNToolTip>
              </Typography>
            </Stack>
            <ReactSortable
              filter=".addImageButtonContainer"
              dragClass="sortableDrag"
              list={waivers.waiversList || []}
              setList={(newList) => setWaivers({ waiversList: newList })}
              onEnd={handleSetData}
              animation="200"
              easing="ease-out"
            >
              {waivers?.waiversList?.map((item, index) => (
                <Item
                  key={index}
                  item={item}
                  draggable
                  classes={classes}
                  handleSwitchChange={(event, item) =>
                    onSwitchChange(event, item)
                  }
                  role={role}
                />
              ))}
            </ReactSortable>
          </>
        ) : (
          <Typography variant="body1" sx={{ textAlign: "center", py: 2 }}>
            Waivers not found
          </Typography>
        )}
      </Box>
    </>
  );
}
